/**
 * Leagues (designs/Caged Leagues): every country with a league, with its divisions and players,
 * and the player's own league on top. Other countries' leagues can be browsed; you fight only in
 * your own (docs/game-design.md §7.1).
 */
import type { CountrySummary } from '@caged/shared';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLeague } from '../../state/league';
import { useApp } from '../../state/store';
import { ArenaBackdrop } from '../components/Backdrop';
import { DesktopHeader, MenuButton } from '../components/Chrome';
import ui from '../components/ui.module.css';
import { countryNames } from '../countries';
import { useLayoutMode, useNow, useScrollTop } from '../hooks';
import { FightChip, Flag, useSeriesTitle } from '../league';
import '../ui.css';
import s from './League.module.css';

export function LeaguesScreen() {
  const { t, i18n } = useTranslation();
  const layout = useLayoutMode();
  const leagueBack = useApp((st) => st.leagueBack);
  const openLeague = useApp((st) => st.openLeague);
  const mine = useLeague((st) => st.mine);
  const load = useLeague((st) => st.load);
  const title = useSeriesTitle();
  const now = useNow(30_000);
  const [countries, setCountries] = useState<CountrySummary[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState('');
  useScrollTop();
  const names = useMemo(() => countryNames(i18n.language), [i18n.language]);

  useEffect(() => {
    void load();
    import('../../net/leagueApi')
      .then((api) => api.fetchCountries())
      .then((r) => setCountries(r.countries), () => setFailed(true));
  }, [load]);

  const q = query.trim().toLocaleLowerCase(i18n.language);
  const list = (countries ?? []).filter((c) => !q || names.name(c.country).toLocaleLowerCase(i18n.language).includes(q));
  const fighters = (countries ?? []).reduce((a, c) => a + c.humans, 0);
  const own = mine?.status === 'ok' ? mine : null;

  const body = (
    <div className={s.stack}>
      {own && (
        <button type="button" className={s.yours} onClick={() => openLeague('league')}>
          <Flag country={own.country} className={s.bigFlag} />
          <span className={s.yoursText}>
            <span className={ui.label}>{t('screens.leagues.yours')}</span>
            <span className={s.yoursName}>{names.name(own.country)}</span>
            <span className={s.meta}>
              {title(own.series)} · {t('screens.league.youPos', { pos: own.table.find((r) => r.me)?.position ?? '–' })}
            </span>
          </span>
          <span className={[ui.arrow, s.amber].join(' ')} aria-hidden="true">
            →
          </span>
        </button>
      )}
      <input
        className={s.search}
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('screens.leagues.search')}
        aria-label={t('screens.leagues.search')}
      />
      <ul className={[ui.card, s.list].join(' ')}>
        {list.map((c) => (
          <li key={c.country}>
            <button type="button" className={s.listRow} onClick={() => openLeague('country', { country: c.country })}>
              <Flag country={c.country} className={s.bigFlag} />
              <span className={s.listText}>
                <span className={s.listName}>{names.name(c.country)}</span>
                <span className={s.meta}>
                  {t('screens.leagues.divisionsCount', { count: c.divisions })} · {t('screens.leagues.playersCount', { count: c.humans })}
                </span>
              </span>
              <span className={s.chev} aria-hidden="true">
                →
              </span>
            </button>
          </li>
        ))}
        {countries && !list.length && <li className={s.empty}>{q ? t('screens.leagues.empty') : t('screens.leagues.none')}</li>}
        {!countries && <li className={s.empty}>{failed ? t('screens.league.failed') : t('screens.league.loading')}</li>}
      </ul>
    </div>
  );

  const head = (
    <div className={s.pageHead}>
      <h1 className={s.pageTitle}>{t('screens.leagues.title')}</h1>
      {countries && (
        <span className={s.meta}>
          {t('screens.leagues.countriesCount', { count: countries.length })} · {t('screens.leagues.playersCount', { count: fighters })}
        </span>
      )}
    </div>
  );

  if (layout === 'desktop') {
    return (
      <div className={['cg-ui', ui.screen].join(' ')}>
        <DesktopHeader />
        <div className={s.deskColumn}>
          <div className={s.deskHeroTop}>
            <button type="button" className={s.backLink} onClick={leagueBack}>
              ← {t('common.back')}
            </button>
            {own && <FightChip next={own.next} now={now} />}
          </div>
          {head}
          {body}
        </div>
      </div>
    );
  }

  return (
    <div className={['cg-ui', ui.screen].join(' ')}>
      <div className={s.phoneBg} aria-hidden="true">
        <ArenaBackdrop tone="menu" shade="linear-gradient(180deg,rgba(20,19,18,.8),rgba(20,19,18,.3) 45%,#141312)" />
      </div>
      <div className={ui.column}>
        <div className={s.topBar}>
          <button type="button" className={ui.iconBtn} onClick={leagueBack} aria-label={t('common.back')}>
            <span aria-hidden="true">←</span>
          </button>
          {own && <FightChip next={own.next} now={now} compact />}
          <MenuButton />
        </div>
        {head}
        {body}
      </div>
    </div>
  );
}
